<template>
	<section>

		<button :disabled="disabled" class="btn btn-light-blue ml-auto mr-2" @click="openApplicationAuditModal">
			{{ role == 'auditor' ? "Application Audit" : "Broker Checklist" }}
		</button>

		<x-modal
			:wWidth="1080"
			v-if="visible"
			@cancel="visible = false"
			@close="visible = false"
			@ok="saveAudit"
			@okSecondary="openInWindow"
			:draggable="true"
			:resizable="true"
			:loading="loading"
			:okDisabled="buttonDisabled || loading"
			:okText="role == 'auditor' ? 'Submit Audit' : 'Submit Checklist'"
            :hideOkSecondary="canStore ? false : true"
            okSecondaryText="Open in External Window"
            okSecondaryType="primary"
		>
			<div slot="title" :class="`d-flex justify-content-between align-items-center`">
				{{ role == 'auditor' ? "Application Audit" : "Broker Checklist" }}
			</div>

			<div class="container">
				<el-form ref="form" label-position="top">
					<el-form-item
						class="category"
						v-for="(groupedAuditQuestion, category) in groupedApplicationAuditQuestions"
						:key="category"
						:label="category"
						:prop="category"
					>
						<el-row class="question" v-for="(auditQuestion, key) in groupedAuditQuestion" :key="key" :gutter="10">
							<el-col style="line-height: 1.6" :span="18">
								{{ auditQuestion.question }}
							</el-col>
							<el-col class="d-flex justify-content-end" style="line-height: 1.6" :span="6">
								<el-radio-group v-model="form.answers[auditQuestion.uid]">
									<el-radio-button label="yes">Yes</el-radio-button>
									<el-radio-button label="no">No</el-radio-button>
									<el-radio-button label="n/a">N/A</el-radio-button>
								</el-radio-group>
							</el-col>
						</el-row>
					</el-form-item>

					<el-form-item label="Additional Comments">
						<el-input :rows="6" type="textarea" v-model="form.comment"></el-input>
					</el-form-item>

					<el-form-item v-if="this.role == 'auditor'" class="category" label="Audit Outcome" prop="Audit Outcome">
						<el-row class="question" :gutter="10">
							<el-col style="line-height: 1.6" :span="18">Does this application pass the audit?</el-col>
							<el-col class="d-flex justify-content-end" style="line-height: 1.6" :span="6">
								<el-radio-group v-model="form.passed">
									<el-radio-button label="yes">Yes</el-radio-button>
									<el-radio-button label="no">No</el-radio-button>
								</el-radio-group>
							</el-col>
						</el-row>
					</el-form-item>
				</el-form>
			</div>
		</x-modal>
	</section>
</template>

<script>
	import { mapGetters } from "vuex"
	export default {
		name: "ApplicationAudit",
		components: {},
		props: {
			role: {
				required: true,
				type: String,
			},
            disabled: {
                required: false,
                type: Boolean,
                default: false
            }
		},
		data() {
			return {
				visible: false,
				loading: false,
			}
		},
        watch: {
            form: {
                handler(form) {
                   if (form) { 
                        localStorage.setItem('application-audit', JSON.stringify(form))
                    }
                },
                deep: true
            },
            role: {
                handler(role) {
                    if (role) {
                        this.form.role = this.role == 'auditor' ? "auditor" : "broker"
                        localStorage.setItem('application-audit-role', role)
                    }
                },
                deep: true,
                immediate: true,
            },
            applicationAuditQuestions: {
                handler(applicationAuditQuestions) {
                    if (applicationAuditQuestions) {
                        localStorage.setItem('application-audit-questions', JSON.stringify(applicationAuditQuestions))
                    }
                },   
                deep: true,
                immediate: true
            },
            applicationType: {
                handler(applicationType) {
                    this.form.type = applicationType
                },
                deep: true,
                immediate: true,
            }
        },
		computed: {
			...mapGetters({
				applicationAuditQuestions: "applicationAudit/getApplicationAuditQuestions",
				user: "user/user",
			}),
            form: {
                set(form) {
                    this.$store.dispatch('applicationAudit/setForm', form)
                },
                get() {
                    return this.$store.getters['applicationAudit/getForm']
                }
            },
            canStore() {
                var test = 'test';
                try {
                    localStorage.setItem(test, test);
                    localStorage.removeItem(test);
                    return true;
                } catch(e) {
                    return false;
                }
            },
            groupedApplicationAuditQuestions() {
                return this.role == 'auditor' ? this.groupedAuditorAuditQuestions : this.groupedBrokerAuditQuestions
            },
            groupedAuditorAuditQuestions() {
                if (_.isEmpty(this.applicationAuditQuestions.auditorAuditQuestions)) return []
				let orderedByCategoryWeight = _.orderBy(this.applicationAuditQuestions.auditorAuditQuestions, "auditCategory.weight")
				return _.groupBy(orderedByCategoryWeight, "auditCategory.name")
            },
            groupedBrokerAuditQuestions() {
                if (_.isEmpty(this.applicationAuditQuestions.brokerAuditQuestions)) return []
				let orderedByCategoryWeight = _.orderBy(this.applicationAuditQuestions.brokerAuditQuestions, "auditCategory.weight")
				return _.groupBy(orderedByCategoryWeight, "auditCategory.name")
            },
			buttonDisabled() {
				let answersCount = _.size(this.form.answers)
				if (this.role == 'auditor') {
                    let applicationAuditQuestionCount = _.size(this.applicationAuditQuestions.auditorAuditQuestions)
					if (answersCount === applicationAuditQuestionCount && this.form.passed) return false
				} else {
                    let applicationAuditQuestionCount = _.size(this.applicationAuditQuestions.brokerAuditQuestions)
					if (answersCount === applicationAuditQuestionCount) return false
				}
				return true
			},
			isBroker() {
				let roles = this.$store.state.user_roles.roles
				let broker = roles.find((item) => {
					if (item.name === "broker") {
						return item
					}
				})
				return !!broker
			},
			isAuditor() {
				let roles = this.$store.state.user_roles.roles
				let auditor = roles.find((item) => {
					if (item.name === "auditor") {
						return item
					}
				})
				return !!auditor
			},
			canDoAudit() {
				let permissions = this.$store.state.user.user_permissions
				let canDoAudit = permissions.find((permission) => {
					if (permission.name === "do audit") {
						return permission
					}
				})
				return !!canDoAudit
			},
			canViewAudit() {
				let permissions = this.$store.state.user.user_permissions
				let canDoAudit = permissions.find((permission) => {
					if (permission.name === "view audit") {
						return permission
					}
				})
				return !!canDoAudit
			},
			applicationType() {
				let applicationTypeId = this.$store.getters.initAppData.application_type_id
				return applicationTypeId == 1 ? "consumer" : "commercial"
			},
		},
		mounted() {
			this.$store.dispatch("applicationAudit/setApplicationAuditQuestions", {
				applicationType: this.applicationType,
				role: this.role,
			})
		},
		methods: {
            popupCenter(url, title, w, h) {
                // Fixes dual-screen position                             Most browsers      Firefox
                const dualScreenLeft = window.screenLeft !==  undefined ? window.screenLeft : window.screenX;
                const dualScreenTop = window.screenTop !==  undefined   ? window.screenTop  : window.screenY;

                const width = window.innerWidth ? window.innerWidth : document.documentElement.clientWidth ? document.documentElement.clientWidth : screen.width;
                const height = window.innerHeight ? window.innerHeight : document.documentElement.clientHeight ? document.documentElement.clientHeight : screen.height;

                const systemZoom = width / window.screen.availWidth;
                const left = (width - w) / 2 / systemZoom + dualScreenLeft
                const top = (height - h) / 2 / systemZoom + dualScreenTop
                const newWindow = window.open(url, title,
                `
                scrollbars=yes,
                directories=no,
                titlebar=no,
                toolbar=no,
                location=0,
                status=no,
                menubar=no,
                scrollbars=no,
                resizable=no,
                width=${w / systemZoom},
                height=${h / systemZoom},
                top=${top},
                left=${left}
                `
                )

                if (window.focus) newWindow.focus();
            },
			openApplicationAuditModal() {
                if (this.form.role !== this.role) {
                    if (this.canStore) {
                        if (localStorage.hasOwnProperty('application-audit')) {
                            localStorage.removeItem('application-audit')
                        }
                        if (localStorage.hasOwnProperty('application-audit-questions')) {
                            localStorage.removeItem('application-audit-questions')
                        }
                        if (localStorage.hasOwnProperty('application-audit-role')) {
                            localStorage.removeItem('application-audit-role')
                        }
                    }
                    this.form.answers = {}
                }
                if (localStorage.hasOwnProperty('application-audit')) {
                    this.form = JSON.parse(localStorage.getItem('application-audit'))
                }
                this.form.role = this.role == 'auditor' ? "auditor" : "broker"
				this.visible = true
			},
            async openInWindow() {
                this.visible = false
                if (this.canStore) {
                    localStorage.setItem('application-audit-role', this.role)
                    localStorage.setItem('application-audit-questions', JSON.stringify(this.applicationAuditQuestions))
                    this.popupCenter(`/admin/settings/application-audit/${this.$store.getters.appId}/checklist`, 'Application Audit', 1200, 800)
                }
            },
			saveAudit() {
				this.loading = true

				this.form.applicationId = this.$store.getters.appId
				this.form.userId = this.user.id
				this.form.passed = this.role == 'auditor' ? this.form.passed : true

				axios
					.post("/api/application-audit/save-audit", this.form, {
						headers: { Authorization: `Bearer ${this.$store.getters.token}` },
					})
					.then(
						(response) => {
                            this.visible = false
                            this.loading = false
                            swal({
								title: "Success!",
								type: "success",
								text: this.form.role == 'auditor' ? "The Application Audit has been completed" : "The Broker Checklist has been completed"
							})
							axios
								.post(
									"/api/application-audit/create-audit-pdf",
									{
										auditId: response.data.id,
										applicationId: this.$store.getters.appId,
									},
									{
										headers: { Authorization: `Bearer ${this.$store.getters.token}` },
									}
								)
								.then(
                                    (response) => {
                                        this.$store.dispatch("applicationAudit/setApplicationAudits")
                                    },
                                    (error) => {

                                    }
                                )
						},
						(error) => {
							reject(error)
							this.loading = false
						}
					)
			},
		},
	}
</script>

<style lang="scss" scoped>
	.category {
		.question {
			margin-bottom: 10px;
			padding-bottom: 10px;
			border-bottom: 1px solid #cccccc;
			display: flex;
			align-items: center;
			justify-content: space-between;

			&:first-child {
				padding-top: 10px;
				border-top: 1px solid #cccccc;
			}
		}
	}
</style>
